import React from "react";

/**
 * Variants Section Component
 * Lets a product have multiple priced variants (e.g. Half/Full, 500ml/1L)
 * instead of a single price, reusing the same Product.variants field the
 * bulk Price Update screen already edits. The Unit dropdown is populated
 * from the Variant Units master list (Settings page), not a hardcoded
 * list, so admins can rename/add options without a code change.
 */
const VariantsSection = ({
  isMultiVariant,
  variants,
  variantUnits = [],
  productPackageCharge = "",
  disabled,
  currencySymbol,
  onToggle,
  onVariantChange,
  onAddVariant,
  onRemoveVariant,
}) => {
  return (
    <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <label className="flex items-center space-x-3 cursor-pointer mb-3">
        <input
          type="checkbox"
          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          checked={isMultiVariant}
          onChange={onToggle}
          disabled={disabled}
        />
        <span className="text-sm font-semibold text-gray-700">
          Multiple Variants (different sizes/quantities)
        </span>
      </label>

      {isMultiVariant && (
        <div className="space-y-3">
          {variants.map((variant, index) => (
            <div
              key={index}
              className="p-3 bg-white rounded-lg border border-gray-200 space-y-2"
            >
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-20">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Qty
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={variant.quantity}
                  onChange={(e) =>
                    onVariantChange(index, "quantity", e.target.value)
                  }
                  disabled={disabled}
                  placeholder="1"
                />
              </div>

              <div className="w-32">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Unit
                </label>
                <select
                  className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={variant.unit}
                  onChange={(e) =>
                    onVariantChange(index, "unit", e.target.value)
                  }
                  disabled={disabled}
                >
                  <option value="">Select</option>
                  {variantUnits.map((u) => (
                    <option key={u._id || u.name} value={u.displayName}>
                      {u.displayName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="w-28">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Price
                </label>
                <div className="flex items-center border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <span className="px-2 text-gray-500 text-sm border-r">
                    {currencySymbol}
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="w-full px-2 py-2 text-sm outline-none bg-transparent"
                    value={variant.price}
                    onChange={(e) =>
                      onVariantChange(index, "price", e.target.value)
                    }
                    disabled={disabled}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="w-28">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Offer Price
                </label>
                <div className="flex items-center border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <span className="px-2 text-gray-500 text-sm border-r">
                    {currencySymbol}
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="w-full px-2 py-2 text-sm outline-none bg-transparent"
                    value={variant.offerPrice}
                    onChange={(e) =>
                      onVariantChange(index, "offerPrice", e.target.value)
                    }
                    disabled={disabled}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="w-28">
                <label
                  className="block text-xs font-medium text-gray-500 mb-1"
                  title="Leave blank to use the product's package charge"
                >
                  Pkg Charge
                </label>
                <div className="flex items-center border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <span className="px-2 text-gray-500 text-sm border-r">
                    {currencySymbol}
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="w-full px-2 py-2 text-sm outline-none bg-transparent"
                    value={variant.packageCharge ?? ""}
                    onChange={(e) =>
                      onVariantChange(index, "packageCharge", e.target.value)
                    }
                    disabled={disabled}
                    placeholder={
                      productPackageCharge !== "" && productPackageCharge != null
                        ? String(productPackageCharge)
                        : "0.00"
                    }
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={() => onRemoveVariant(index)}
                disabled={disabled || variants.length <= 1}
                className="p-2 text-red-600 hover:bg-red-50 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                title="Remove variant"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Description (optional)
                </label>
                <input
                  type="text"
                  className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={variant.description || ""}
                  onChange={(e) =>
                    onVariantChange(index, "description", e.target.value)
                  }
                  disabled={disabled}
                  placeholder="e.g. Serves 1, boneless"
                />
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={onAddVariant}
            disabled={disabled}
            className="px-3 py-2 text-sm font-medium text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Add Variant
          </button>
        </div>
      )}
    </div>
  );
};

export default VariantsSection;
